import { create } from "zustand";
import dayjs from "dayjs";
import { message } from "antd";
import { getScanApi, getScanSaveApi } from "../services/nearToExpiry";

export const useNearToExpiryScanStore = create((set, get) => ({
  /* ================= FORM STATE ================= */
  formData: {
    StoreCode: null,
    searchBy: "Barcode",
    date: dayjs(),
    searchBarCode: "",
  },

  formErrors: {},
  handleCriticalFieldChange: (field, value) => {
    const { gridData } = get();

    if (gridData.length > 0) {
      message.warning("Store or Date changed. Scanned data has been cleared.");

      set({
        gridData: [],
        multiOptions: [],
        modalVisible: false,
      });
    }

    set((state) => ({
      formData: {
        ...state.formData,
        [field]: value,
      },
      formErrors: {
        ...state.formErrors,
        [field]: "",
      },
    }));
  },

  setFormField: (field, value) =>
    set((state) => ({
      formData: { ...state.formData, [field]: value },
      formErrors: { ...state.formErrors, [field]: "" },
    })),

  resetBarcode: () =>
    set((state) => ({
      formData: { ...state.formData, searchBarCode: "" },
    })),

  /* ================= GRID STATE ================= */
  gridData: [],
  multiOptions: [],
  modalVisible: false,
  qtyErrorModal: false,
  qtyErrorMsg: "",

  setQtyErrorModal: (val) => set({ qtyErrorModal: val }),

  /* ================= VALIDATION ================= */
  validateForm: () => {
    const { formData } = get();
    const errors = {};

    if (!formData.StoreCode) errors.StoreCode = "Store is required";
    if (!formData.searchBy) errors.searchBy = "Type is required";
    // if (!formData.divisionCode) errors.divisionCode = "Division is required";
    if (!formData.date) errors.date = "Date is required";

    set({ formErrors: errors });
    return Object.keys(errors).length === 0;
  },

  /* ================= BARCODE SCAN ================= */
  handleBarcodeScan: async () => {
    const { formData } = get();

    if (!get().validateForm()) return;
    if (!formData.searchBarCode) return;

    const payload = {
      location: formData.StoreCode,
      date: dayjs(formData.date).format("YYYY-MM-DD"),
      searchBy: formData.searchBy,
      searchValue: formData.searchBarCode,
    };

    try {
      const res = await getScanApi(payload);
      const data = res?.Data || [];

      if (!data.length || data[0]?.Status === "0") {
        message.warning("No data found");
        get().resetBarcode();
        return;
      }

      if (data.length === 1) {
        get().addOrUpdateRow(data[0]);
      } else {
        set({ multiOptions: data, modalVisible: true });
      }
    } catch {
      message.error("Scan failed");
    } finally {
      get().resetBarcode(); // ✅ barcode empty after scan
    }
  },

  /* ================= ADD / UPDATE ================= */
  addOrUpdateRow: (item) => {
    set((state) => {
      const prev = state.gridData;
      const index = prev.findIndex((r) => r.NearExpiryID === item.NearExpiryID);

      const expiryQty = item.ExpiryQty ?? 0;

      if (index >= 0) {
        const nextQty = (prev[index].ScanQty || 0) + 1;

        if (nextQty > expiryQty) {
          return { qtyErrorModal: true };
        }

        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          ScanQty: nextQty,
          scanqty: nextQty,
        };

        return {
          gridData: updated,
          modalVisible: false, // ✅ CLOSE MODAL
          multiOptions: [],
        };
      }

      if (1 > expiryQty) return { qtyErrorModal: true };

      return {
        gridData: [
          ...prev,
          {
            ...item,
            serialNo: prev.length + 1,
            ScanQty: 1,
            scanqty: 1,
          },
        ],
        modalVisible: false,
        multiOptions: [],
      };
    });
  },

  /* ================= SAVE ================= */
  saveScanData: async () => {
    const { gridData, formData } = get();

    if (!gridData.length) {
      message.warning("No scanned data to save");
      return;
    }

    const payload = gridData.map((row) => ({
      locationCode: formData.StoreCode,
      populatedDate: dayjs(formData.date).format("YYYY-MM-DD"),
      manufacturingDate: row.ManufacturingDate,
      articleCode: row.ArticleCode,
      expiryType: row.ExpiryType,
      scanQty: row.ScanQty,
      scanMrp: row.MRP,
    }));

    try {
      const response = await getScanSaveApi(payload);
      if (response?.IsSuccess) {
        message.success(
          response?.DisplayMessage || "Scanned data saved successfully",
        );

        set({
          gridData: [],
          formData: { ...get().formData, searchBarCode: "" },
        });
      } else {
        message.error(
          response?.DisplayMessage || "Failed to save scanned data",
        );
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.DisplayMessage ||
        error?.response?.data?.ErrorMessage?.[0] ||
        error.message ||
        "Save failed";

      message.error(errMsg);
    }
  },
  updateScanQty: (nearExpiryId, qty) => {
    set((state) => {
      const updated = state.gridData.map((row) => {
        if (row.NearExpiryID !== nearExpiryId) return row;

        const expiryQty = row.ExpiryQty ?? 0;

        if (qty > expiryQty) {
          set({
            qtyErrorModal: true,
            qtyErrorMsg: "Scan Qty cannot be greater than Expiry Qty",
          });
          return row;
        }

        return {
          ...row,
          ScanQty: qty,
          scanqty: qty,
        };
      });

      return { gridData: updated };
    });
  },
  showQtyError: (msg) =>
    set({
      qtyErrorModal: true,
      qtyErrorMsg: msg,
    }),

  closeQtyError: () =>
    set({
      qtyErrorModal: false,
      qtyErrorMsg: "",
    }),

  /* ================= DELETE ROW ================= */
  deleteRowById: (nearExpiryId) => {
    set((state) => {
      const filtered = state.gridData.filter(
        (row) => row.NearExpiryID !== nearExpiryId,
      );

      // serialNo re-generate
      const updatedWithSerial = filtered.map((row, index) => ({
        ...row,
        serialNo: index + 1,
      }));

      return { gridData: updatedWithSerial };
    });
  },
}));
